import React, { useState, useEffect } from 'react';
import { Card, Typography, List, Tag, Empty, Spin, Divider, Button, Modal, Form, Input, Select, DatePicker, message } from 'antd';
import { CalendarOutlined, ClockCircleOutlined, ProjectOutlined, EditOutlined, SaveOutlined, CloseOutlined } from '@ant-design/icons';
import { useTimesheetStore } from '../../store/useTimesheetStore';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const TodayTimesheets = ({ user , callTodayTimesheets }) => {
  const { fetchTimesheets, updateTimesheet, projects, fetchProjects, loading } = useTimesheetStore();
  const [todayTimesheets, setTodayTimesheets] = useState([]);
  const [editingTimesheet, setEditingTimesheet] = useState(null);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [form] = Form.useForm();
  
  useEffect(() => {
    if (user?.id) {
      const today = dayjs().format('YYYY-MM-DD');
      fetchTodayTimesheets(today);
      fetchProjects(); // Fetch projects for edit form
    }
  }, [user ,callTodayTimesheets]);

  const fetchTodayTimesheets = async (date) => {
    try {
      const result = await fetchTimesheets(user.id, date, date);
      if (result.success) {
        setTodayTimesheets(result.data || []);
      }
    } catch (error) {
      console.error('Error fetching today timesheets:', error);
      setTodayTimesheets([]);
    }
  };
 
  
  const getTotalHours = () => {
    return Array.isArray(todayTimesheets?.data) ? todayTimesheets?.data.reduce((total, timesheet) => total + Number(timesheet.hours || 0), 0) : 0;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'submitted': return 'blue';
      case 'approved': return 'green';
      case 'rejected': return 'red';
      default: return 'default';
    }
  };
  
  const handleEditTimesheet = (timesheet) => {
    setEditingTimesheet(timesheet);
    form.setFieldsValue({
      projectId: timesheet.projectId._id,
      date: dayjs(timesheet.date),
      hours: timesheet.hours,
      description: timesheet.description
    });
    setIsEditModalVisible(true);
  };

  const handleUpdateTimesheet = async (values) => {
    try {
      const updatedData = {
        ...values,
        date: values.date.format('YYYY-MM-DD'),
        projectName: Array.isArray(projects?.data) ? projects?.data.find(p => p._id === values.projectId)?.name : ''
      };
      
      const result = await updateTimesheet(editingTimesheet._id, updatedData);
      
      if (result.success) {
        message.success('Timesheet updated successfully');
        setIsEditModalVisible(false);
        form.resetFields();
        setEditingTimesheet(null);
        // Refresh today's timesheets
        const today = dayjs().format('YYYY-MM-DD');
        fetchTodayTimesheets(today);
      } else {
        message.error(result.error || 'Failed to update timesheet');
      }
    } catch (error) {
      message.error('Failed to update timesheet');
    }
  };

  const handleCancelEdit = () => {
    setIsEditModalVisible(false);
    form.resetFields();
    setEditingTimesheet(null);
  }; 
  return (
    <Card className="shadow-lg h-full">
      <div className="text-center mb-6">
        <Title level={2} className="text-gray-900 mb-2">
          <CalendarOutlined className="mr-2" />
          Today's Timesheets
        </Title>
        <Text className="text-gray-600">
          {dayjs().format('MMMM DD, YYYY')}
        </Text>
      </div>

      {loading ? (
        <div className="text-center py-8">
          <Spin size="large" />
          <div className="mt-4">
            <Text className="text-gray-500">Loading today's timesheets...</Text>
          </div>
        </div>
      ) : !Array.isArray(todayTimesheets?.data) || todayTimesheets?.data.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <span className="text-gray-500">
              No timesheets submitted for today
            </span>
          }
        />
      ) : (
        <>
          {Array.isArray(todayTimesheets?.data) && todayTimesheets?.data.length > 0 && (
          <div className="mb-4 p-4 bg-blue-50 rounded-lg">
            <div className="flex justify-between items-center">
              <Text className="text-blue-800 font-medium">
                <ClockCircleOutlined className="mr-2" />
                Total Hours Today:
              </Text>
              <Text className="text-blue-900 text-lg font-bold">
                {getTotalHours()} hours
              </Text>
            </div>
          </div>
        )}
          {Array.isArray(todayTimesheets?.data) && todayTimesheets?.data.length > 0 && <Divider />}
          <div className="max-h-96 overflow-y-auto">
          <List
            dataSource={Array.isArray(todayTimesheets?.data) ? todayTimesheets?.data : []}
            renderItem={(timesheet) => (
              <List.Item className="border-b border-gray-100 last:border-b-0">
                <div className="w-full">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1">
                      <Text strong className="text-gray-900">
                        <ProjectOutlined className="mr-2" />
                        {timesheet.projectName}
                      </Text>
                      <div className="mt-1">
                        <Tag color={getStatusColor(timesheet.status)}>
                          {timesheet.status?.toUpperCase()}
                        </Tag>
                      </div>
                    </div>
                    <div className="text-right flex items-center gap-2">
                      <Text className="text-lg font-semibold text-blue-600">
                        {timesheet.hours}h
                      </Text>
                      {timesheet.status === 'submitted' && (
                        <Button
                          type="text"
                          size="small"
                          icon={<EditOutlined />}
                          onClick={() => handleEditTimesheet(timesheet)}
                          className="text-gray-500 hover:text-blue-600"
                        />
                      )}
                    </div>
                  </div>
                  
                  {timesheet.description && (
                    <div className="mt-2">
                      <Text className="text-gray-600 text-sm">
                        {timesheet.description}
                      </Text>
                    </div>
                  )}
                  
                  <div className="mt-2 text-xs text-gray-400">
                    Submitted: {dayjs(timesheet.createdAt).format('h:mm A')}
                  </div>
                </div>
              </List.Item>
            )}
          />
          </div>
        </>
      )}
      
      <Modal
        title="Edit Timesheet"
        open={isEditModalVisible}
        onCancel={handleCancelEdit}
        footer={null}
        width={500}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleUpdateTimesheet}
          className="mt-4"
        >
          <Form.Item
            name="projectId"
            label="Project"
            rules={[{ required: true, message: 'Please select a project' }]}
          >
            <Select placeholder="Select project">
              {Array.isArray(projects?.data) && projects?.data.map(project => (
                <Select.Option key={project._id} value={project._id}>
                  {project.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="date"
            label="Date"
            rules={[{ required: true, message: 'Please select a date' }]}
          >
            <DatePicker className="w-full" />
          </Form.Item>

          <Form.Item
            name="hours"
            label="Hours"
            rules={[
                    { required: true, message: 'Please enter hours worked' },
                    {
                      validator: (_, value) => {
                        if (value === undefined || value === null) {
                          return Promise.reject(new Error('Please enter hours worked'));
                        }
                        if (value < 0.5 || value > 8) {
                          return Promise.reject(new Error('Hours must be between 0.5 and 8'));
                        }
                        return Promise.resolve();
                      }
                    }
                  ]}
          >
            <Input
              type="number"
              step="0.5"
              min="0.5"
              max="8"
              placeholder="Enter hours worked"
            />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
          >
            <Input.TextArea
              rows={3}
              placeholder="Enter work description (optional)"
            />
          </Form.Item>

          <Form.Item className="mb-0 text-right">
            <div className="flex justify-end gap-2">
              <Button icon={<CloseOutlined />} onClick={handleCancelEdit}>
                Cancel
              </Button>
              <Button type="primary" icon={<SaveOutlined />} htmlType="submit" loading={loading}>
                Update Timesheet
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default TodayTimesheets;
